import { useCallback, useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router"
import { TEnkIWPMelding, TEnkIWPTerugkoppeling, EnkIWPServerMeldingen as ApiMeldingen } from "../api/api-iwp-server"
import EnkPageContainer from "../components/page-container"
import EnkContentLoadingAndError from "../components/error-box"
import { useAuth } from "../auth/use-auth"
import { ArrowLeftIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline"

const statusStyles: Record<number, { class: string; text: string }> = {
  10: { class: "text-blue-700 bg-blue-50 ring-blue-600/20", text: "Nieuw" },
  20: { class: "text-yellow-700 bg-yellow-50 ring-yellow-600/20", text: "Onderhanden" },
  30: { class: "text-green-700 bg-green-50 ring-green-600/20", text: "Afgesloten" },
}

const prioriteitStyles: Record<number, { class: string; text: string }> = {
  1: { class: "text-red-700 bg-red-50 ring-red-600/20", text: "Hoog" },
  2: { class: "text-gray-700 bg-gray-50 ring-gray-500/10", text: "Normaal" },
  3: { class: "text-blue-600 bg-blue-50 ring-blue-500/10", text: "Laag" },
}

function StatusBadge({ status }: { status: number }) {
  const style = statusStyles[status] || { class: "text-gray-600 bg-gray-50 ring-gray-500/10", text: status.toString() }
  return (
    <span className={`${style.class} inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset`}>
      {style.text}
    </span>
  )
}

function PrioriteitBadge({ prioriteit }: { prioriteit: number }) {
  if (!prioriteit) return null
  const style = prioriteitStyles[prioriteit] || { class: "text-gray-600 bg-gray-50 ring-gray-500/10", text: prioriteit.toString() }
  return (
    <span className={`${style.class} inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset`}>
      {style.text}
    </span>
  )
}

function stripRtf(rtf: string): string {
  if (!rtf || !rtf.startsWith("{\\rtf")) return rtf || ""
  // Strip RTF control words en groepen, behoud platte tekst
  let text = rtf
    .replace(/\{\\fonttbl[^}]*\}/g, "")
    .replace(/\{\\colortbl[^}]*\}/g, "")
    .replace(/\{\\[*]?[^}]*\}/g, "")
    .replace(/\\par\b/g, "\n")
    .replace(/\\tab\b/g, "\t")
    .replace(/\\[a-z]+\d*\s?/gi, "")
    .replace(/[{}]/g, "")
    .trim()
  return text
}

function formatDate(dateStr: string) {
  if (!dateStr) return "-"
  const date = new Date(dateStr)
  return date.toLocaleDateString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
}

function TerugkoppelingBericht({ tk }: { tk: TEnkIWPTerugkoppeling }) {
  const isKlant = tk.bron === "W"

  return (
    <div className={`flex ${isKlant ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[75%] rounded-lg px-4 py-3 ${
        isKlant
          ? "bg-primary text-white"
          : "bg-gray-100 text-gray-900"
      }`}>
        <div className={`text-xs font-medium mb-1 ${isKlant ? "text-white/80" : "text-gray-500"}`}>
          {tk.doorNaam} &middot; {formatDate(tk.aanmaakDatum)}
        </div>
        <p className="text-sm whitespace-pre-wrap">{tk.tekst}</p>
      </div>
    </div>
  )
}

export default function EnkMeldingDetail() {
  const { meldingnr } = useParams<{ meldingnr: string }>()
  const navigate = useNavigate()
  const { getCurrentGroupId } = useAuth()

  const [melding, setMelding] = useState<TEnkIWPMelding | null>(null)
  const [terugkoppelingen, setTerugkoppelingen] = useState<TEnkIWPTerugkoppeling[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [replyText, setReplyText] = useState("")
  const [klantMagReageren, setKlantMagReageren] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const nr = Number(meldingnr)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const groupId = getCurrentGroupId()
      const [detail, tks] = await Promise.all([
        ApiMeldingen.getDetail(groupId, nr),
        ApiMeldingen.getTerugkoppelingen(groupId, nr),
      ])
      setMelding(detail)
      setTerugkoppelingen(tks)
      setError(null)
    } catch (err) {
      console.error("Failed to fetch melding detail:", err)
      setError("Er is een fout opgetreden bij het ophalen van de melding.")
    } finally {
      setLoading(false)
    }
  }, [getCurrentGroupId, nr])

  useEffect(() => {
    if (nr > 0) fetchData()
  }, [fetchData, nr])

  const isIntern = melding?.isIntern ?? false

  // Sorteer op datum zodat we de nieuwste terugkoppeling betrouwbaar vinden
  const gesorteerd = [...terugkoppelingen].sort((a, b) => new Date(a.aanmaakDatum).getTime() - new Date(b.aanmaakDatum).getTime())
  const laatsteTk = gesorteerd.length > 0 ? gesorteerd[gesorteerd.length - 1] : null

  // Interne gebruikers mogen altijd reageren, externe alleen als KlantMagReageren=True op het laatste bericht
  const magReageren = isIntern || (laatsteTk?.klantMagReageren ?? false)

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!replyText.trim()) return
    setSubmitting(true)
    try {
      await ApiMeldingen.createTerugkoppeling(getCurrentGroupId(), { meldingnr: nr, tekst: replyText, klantMagReageren: isIntern ? klantMagReageren : false })
      setReplyText("")
      fetchData()
    } catch (err) {
      console.error("Failed to create terugkoppeling:", err)
      setError("Er is een fout opgetreden bij het versturen van uw reactie.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <EnkPageContainer>
      <button
        type="button"
        onClick={() => navigate("/meldingen")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Terug naar meldingen
      </button>

      <EnkContentLoadingAndError loading={loading} error={error}>
        {melding && (
          <div className="space-y-6">
            {/* Melding info */}
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    #{melding.meldingnr} &mdash; {melding.omschrijving}
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Door {melding.meldingDoor} op {formatDate(melding.aanmaakDatum)}
                  </p>
                </div>
                <StatusBadge status={melding.recordstatus} />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                {melding.isIntern && melding.relatienaam && (
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Klant</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">{melding.relatienaam}</dd>
                  </div>
                )}
                {melding.meldingsoort && (
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Meldingsoort</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">{melding.meldingsoortLabel || melding.meldingsoort}</dd>
                  </div>
                )}
                {melding.prioriteit > 0 && (
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Prioriteit</dt>
                    <dd className="mt-0.5"><PrioriteitBadge prioriteit={melding.prioriteit} /></dd>
                  </div>
                )}
                {melding.contractnr && (
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Servicecontract</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">{melding.contractnr} - {melding.contractOmschrijving}</dd>
                  </div>
                )}
                {melding.installatienr && (
                  <div>
                    <dt className="text-xs font-medium text-gray-500">Installatie</dt>
                    <dd className="mt-0.5 text-sm text-gray-900">{melding.installatienr} - {melding.installatieOmschrijving}</dd>
                  </div>
                )}
              </div>

              {melding.melding && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{stripRtf(melding.melding)}</p>
                </div>
              )}
            </div>

            {/* Terugkoppeling thread */}
            <div className="bg-white rounded-lg shadow-sm ring-1 ring-gray-900/5 p-6">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Terugkoppeling</h3>

              {terugkoppelingen.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">Nog geen terugkoppeling ontvangen</p>
              ) : (
                <div className="space-y-3">
                  {[...terugkoppelingen]
                    .sort((a, b) => new Date(a.aanmaakDatum).getTime() - new Date(b.aanmaakDatum).getTime())
                    .map((tk) => (
                    <TerugkoppelingBericht key={tk.terugkoppelingId} tk={tk} />
                  ))}
                </div>
              )}

              {/* Reply formulier */}
              {magReageren && (
                <form onSubmit={handleReply} className="mt-4 border-t border-gray-100 pt-4">
                  <label htmlFor="reply" className="sr-only">Uw reactie</label>
                  <div className="flex gap-3">
                    <textarea
                      id="reply"
                      rows={2}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder={isIntern ? "Typ uw bericht aan de klant..." : "Typ uw reactie..."}
                      className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                      required
                    />
                    <button
                      type="submit"
                      disabled={submitting || !replyText.trim()}
                      className="self-end rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 disabled:opacity-50"
                    >
                      <PaperAirplaneIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {isIntern && (
                    <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={klantMagReageren}
                        onChange={(e) => setKlantMagReageren(e.target.checked)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      Klant mag reageren
                    </label>
                  )}
                </form>
              )}
            </div>
          </div>
        )}
      </EnkContentLoadingAndError>
    </EnkPageContainer>
  )
}
